/* assets/digital-thinking-ring-sizer.js */
(function () {
  "use strict";

  const EMOLO_SIZES = [
    { size: 8,  mm: 15.2, eu: 48, us: 4.5 },
    { size: 11, mm: 16.2, eu: 51, us: 5.5 },
    { size: 12, mm: 16.5, eu: 52, us: 6 },
    { size: 14, mm: 17.2, eu: 54, us: 7 },
    { size: 16, mm: 17.8, eu: 56, us: 8 },
    { size: 18, mm: 18.4, eu: 58, us: 8.5 },
    { size: 20, mm: 19.0, eu: 60, us: 9.5 },
    { size: 22, mm: 20.0, eu: 62, us: 10 }
  ];

  const REFS = {
    coin_2eur: { type: "circle", mm: 25.75 },
    coin_1eur: { type: "circle", mm: 23.25 },
    coin_50c:  { type: "circle", mm: 24.25 },
    card:      { type: "card",  mm: 85.60 }
  };

  function safeJsonParse(str) { try { return JSON.parse(str); } catch { return null; } }
  function t(i18n, key, fallback) { return (i18n && i18n[key]) ? i18n[key] : (fallback || ""); }
  function format(str, vars) {
    return String(str || "").replace(/\{(\w+)\}/g, (m, k) => (vars && vars[k] !== undefined) ? vars[k] : m);
  }

  function closestSize(dmm) {
    let best = EMOLO_SIZES[0];
    let bestDiff = Math.abs(dmm - best.mm);

    for (const s of EMOLO_SIZES) {
      const diff = Math.abs(dmm - s.mm);
      if (diff < bestDiff) { best = s; bestDiff = diff; }
    }

    const sorted = [...EMOLO_SIZES].sort((a, b) => Math.abs(dmm - a.mm) - Math.abs(dmm - b.mm));
    const a = sorted[0], b = sorted[1];
    const threshold = 0.20;
    const between = (Math.abs(dmm - a.mm) <= threshold && Math.abs(dmm - b.mm) <= threshold) ? { a, b } : null;

    return { best, between };
  }

  function initRingSizer(root) {
    if (!root || root.dataset.dtInit === "1") return;
    root.dataset.dtInit = "1";

    const sectionId = root.getAttribute("data-section-id") || "";

    const i18nEl = root.querySelector("[data-dt-ring-sizer-i18n]");
    const i18n = i18nEl ? (safeJsonParse(i18nEl.textContent || "{}") || {}) : {};

    const stepBtns = root.querySelectorAll(".emolo-rs__step");
    const panels = root.querySelectorAll(".emolo-rs__panel");

    const refSelect   = root.querySelector(`#dtRef-${sectionId}`);
    const calibSlider = root.querySelector(`#dtCalibSlider-${sectionId}`);
    const ringSlider  = root.querySelector(`#dtRingSlider-${sectionId}`);

    const shape = root.querySelector(`#dtShape-${sectionId}`);
    const ring  = root.querySelector(`#dtRing-${sectionId}`);

    const hint      = root.querySelector(`#dtHint-${sectionId}`);
    const calibInfo = root.querySelector(`#dtCalibInfo-${sectionId}`);
    const calibDone = root.querySelector(`#dtCalibDone-${sectionId}`);

    const mmOut    = root.querySelector(`#dtMm-${sectionId}`);
    const sizeOut  = root.querySelector(`#dtSize-${sectionId}`);
    const equivOut = root.querySelector(`#dtEquiv-${sectionId}`);

    if (!refSelect || !calibSlider || !ringSlider || !shape || !ring) return;

    let pxPerMm = null;

    function setStep(n) {
      panels.forEach(p => p.classList.toggle("emolo-rs__panel--active", p.getAttribute("data-panel") === String(n)));
      stepBtns.forEach(b => b.classList.toggle("emolo-rs__step--active", b.getAttribute("data-step") === String(n)));
    }

    function setRefUI() {
      const ref = REFS[refSelect.value] || REFS.coin_2eur;

      if (ref.type === "card") {
        shape.style.setProperty("--emolo-calib-radius", "14px");
        hint.textContent = t(i18n, "hint_card", "Align a bank card's width with the rectangle and adjust.");
      } else {
        shape.style.setProperty("--emolo-calib-radius", "999px");
        hint.textContent = t(i18n, "hint_coin", "Place the coin on top and adjust the size.");
      }

      calibInfo.textContent = t(i18n, "help_done", "When it matches, tap “Done”.");
      updateCalibShape();
    }

    function updateCalibShape() {
      const px = Number(calibSlider.value) || 220;
      const ref = REFS[refSelect.value] || REFS.coin_2eur;

      shape.style.setProperty("--emolo-calib-w", px + "px");
      shape.style.setProperty("--emolo-calib-h", (ref.type === "card" ? (px * 0.63) : px) + "px");
    }

    function computePxPerMm() {
      const ref = REFS[refSelect.value] || REFS.coin_2eur;
      const wpx = shape.getBoundingClientRect().width;
      if (!wpx || !ref.mm) { pxPerMm = null; return; }
      pxPerMm = wpx / ref.mm;
    }

    function configureRingSliderRange() {
      if (!pxPerMm) return;
      const minMm = 13.0;
      const maxMm = 23.0;
      ringSlider.min = String(Math.round(pxPerMm * minMm));
      ringSlider.max = String(Math.round(pxPerMm * maxMm));
      ringSlider.value = String(Math.round(pxPerMm * 17.5));
    }

    function updateRing() {
      const px = Number(ringSlider.value) || 220;
      ring.style.setProperty("--emolo-ring", px + "px");

      if (!pxPerMm) {
        if (mmOut) mmOut.textContent = "—";
        if (sizeOut) sizeOut.textContent = "—";
        if (equivOut) equivOut.textContent = t(i18n, "calibrate_first", "Calibrate first.");
        return;
      }

      const renderedPx = ring.getBoundingClientRect().width;
      const dmm = renderedPx / pxPerMm;

      if (mmOut) {
        mmOut.textContent = format(
          t(i18n, "diameter_value", "Ø {mm} mm (inside diameter)"),
          { mm: dmm.toFixed(1) }
        );
      }

      const { best, between } = closestSize(dmm);
      if (sizeOut) sizeOut.textContent = String(best.size);

      if (equivOut) {
        if (between) {
          equivOut.textContent =
            format(t(i18n, "between", "You're between {a} and {b}."), { a: between.a.size, b: between.b.size }) +
            " " +
            t(i18n, "note_between", "Recommendation: choose the larger.");
        } else {
          equivOut.textContent = format(
            t(i18n, "equiv", "Approx. equivalence: EU {eu} · US {us}"),
            { eu: best.eu, us: best.us }
          );
        }
      }
    }

    refSelect.addEventListener("change", setRefUI);
    calibSlider.addEventListener("input", updateCalibShape);
    ringSlider.addEventListener("input", updateRing);

    calibDone.addEventListener("click", () => {
      computePxPerMm();
      stepBtns.forEach(b => { if (b.getAttribute("data-step") === "2") b.disabled = false; });
      configureRingSliderRange();
      setStep(2);
      updateRing();
    });

    setRefUI();
    updateRing();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll("[data-dt-ring-sizer]").forEach(initRingSizer);
  }

  document.addEventListener("DOMContentLoaded", () => initAll(document));
  document.addEventListener("shopify:section:load", (e) => initAll(e.target));
})();